import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";

export interface Resource {
  path: string;
  title: string;
  description: string;
}

// The SaaS-metrics topic cluster. Every page in it cross-links to the others
// with descriptive anchors so the pages reinforce each other for search.
const CLUSTER: Resource[] = [
  {
    path: "/calculators",
    title: "SaaS calculators",
    description: "Free MRR, ARR, churn rate and MRR growth calculators.",
  },
  {
    path: "/saas-metrics",
    title: "SaaS metrics tracking",
    description: "Track MRR, ARR, churn, NRR, LTV and CAC from one source of truth.",
  },
  {
    path: "/saas-metrics-cheat-sheet",
    title: "SaaS metrics cheat sheet",
    description: "Every metric founders track, with clear formulas and examples.",
  },
  {
    path: "/benchmarks",
    title: "SaaS benchmarks",
    description: "Compare your metrics against peers by ARR stage and vertical.",
  },
];

interface RelatedResourcesProps {
  // Paths to omit (the current page, and any parent already linked elsewhere).
  currentPath: string;
  exclude?: string[];
  // Which cluster to render; defaults to the SaaS-metrics cluster above.
  resources?: Resource[];
}

const RelatedResources = ({
  currentPath,
  exclude = [],
  resources: cluster = CLUSTER,
}: RelatedResourcesProps) => {
  const hidden = new Set([currentPath, ...exclude]);
  const resources = cluster.filter((resource) => !hidden.has(resource.path));

  if (resources.length === 0) {
    return null;
  }

  return (
    <section className="px-6 md:px-10 py-16 md:py-20 border-t border-border">
      <div className="max-w-4xl mx-auto">
        <h2
          className="text-2xl md:text-3xl tracking-tight text-center mb-8"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Related resources
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {resources.map((resource) => (
            <Link
              key={resource.path}
              to={resource.path}
              className="group rounded-xl border border-border bg-card p-6 transition-colors hover:border-foreground/30"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3
                    className="text-lg text-foreground"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {resource.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">{resource.description}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground transition-colors group-hover:text-foreground flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedResources;
